import { useState } from "react";
import { FaSearch } from "react-icons/fa";
import Modal from "src/components/modal/modal";
import type {
  StorePaymentAudit,
  StoreSubscriptionPaymentAudit,
} from "../subscription-users.service";
import { useAuditStoreSubscriptionPaymentsMutation } from "../subscription-users.service";
import type { StoreSubscriptionVerificationTarget } from "./store-subscription-verification";

type AuditButtonProps = {
  isLoading?: boolean;
  onAudit: () => void;
};

type AuditModalProps = {
  isOpen: boolean;
  provider?: StoreSubscriptionVerificationTarget["provider"];
  isLoading: boolean;
  result?: StoreSubscriptionPaymentAudit;
  errorMessage?: string;
  onClose: () => void;
};

const formatDateTime = (value: string | null) =>
  value ? new Date(value).toLocaleString() : "Not available";

const formatAmount = (payment: StorePaymentAudit) =>
  payment.amount === null
    ? "Not available"
    : `${payment.amount} ${payment.currency ?? ""}`.trim();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getStringValue = (value: unknown) =>
  typeof value === "string" && value.trim() ? value : undefined;

const getErrorMessage = (error: unknown) => {
  if (!isRecord(error)) return "Unable to audit store payments.";

  const errorData = error.data;
  const dataError = isRecord(errorData) ? errorData.error : undefined;

  return (
    getStringValue(error.message) ??
    getStringValue(error.error) ??
    getStringValue(errorData) ??
    getStringValue(dataError) ??
    "Unable to audit store payments."
  );
};

const auditStatus = {
  RECORDED: {
    label: "Recorded",
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
  NOT_RECORDED: {
    label: "No Firestore record",
    className: "border-rose-200 bg-rose-50 text-rose-700",
  },
  LEGACY_UNVERIFIABLE: {
    label: "Legacy record cannot be matched",
    className: "border-amber-200 bg-amber-50 text-amber-700",
  },
} as const;

const StorePaymentRow = ({ payment }: { payment: StorePaymentAudit }) => {
  const status = auditStatus[payment.status];

  return (
    <article className="rounded-lg border border-slate-200 p-3 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-slate-900">
            {payment.productId || "Store payment"}
          </p>
          <p className="mt-1 break-all font-mono text-xs text-slate-500">
            {payment.storePaymentId}
          </p>
        </div>
        <span
          className={`rounded border px-2 py-1 text-xs font-medium ${status.className}`}
        >
          {status.label}
        </span>
      </div>
      <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
        <div>
          <dt className="text-slate-500">Purchased</dt>
          <dd className="mt-1 font-medium text-slate-700">
            {formatDateTime(payment.purchasedAt)}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Expires</dt>
          <dd className="mt-1 font-medium text-slate-700">
            {formatDateTime(payment.expiresAt)}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Amount</dt>
          <dd className="mt-1 font-medium text-slate-700">
            {formatAmount(payment)}
          </dd>
        </div>
      </dl>
    </article>
  );
};

export const AuditStoreSubscriptionPaymentsButton = ({
  isLoading = false,
  onAudit,
}: AuditButtonProps) => (
  <button
    type="button"
    onClick={onAudit}
    disabled={isLoading}
    className="inline-flex h-9 items-center justify-center gap-2 rounded border border-violet-200 px-3 text-sm font-medium text-violet-700 transition-colors hover:border-violet-300 hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-50"
  >
    <FaSearch className={isLoading ? "animate-pulse" : ""} />
    Audit payments
  </button>
);

export const StoreSubscriptionPaymentAuditModal = ({
  isOpen,
  provider,
  isLoading,
  result,
  errorMessage,
  onClose,
}: AuditModalProps) => (
  <Modal isOpen={isOpen} onClose={onClose} size={2}>
    <div className="pr-8">
      <p className="text-sm font-medium text-violet-700">Store payment audit</p>
      <h2 className="mt-1 text-xl font-semibold text-slate-900">
        {provider} subscription
      </h2>
      <p className="mt-2 text-sm text-slate-600">
        This check is read-only and does not create or update payment records.
      </p>
    </div>

    <div className="mt-6">
      {isLoading && (
        <div className="text-sm text-slate-600">Checking store payments...</div>
      )}
      {errorMessage && (
        <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {errorMessage}
        </div>
      )}
      {result && (
        <>
          <p className="mb-3 text-sm text-slate-600">
            {result.localPaymentCount} related Firestore payment
            {result.localPaymentCount === 1 ? "" : "s"}
          </p>
          {result.storePayments.length ? (
            <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1">
              {result.storePayments.map((payment) => (
                <StorePaymentRow
                  key={payment.storePaymentId}
                  payment={payment}
                />
              ))}
            </div>
          ) : (
            <p className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
              No store payment records were returned by this audit.
            </p>
          )}
        </>
      )}
    </div>
  </Modal>
);

export const useStoreSubscriptionPaymentAudit = () => {
  const [target, setTarget] = useState<StoreSubscriptionVerificationTarget>();
  const [result, setResult] = useState<StoreSubscriptionPaymentAudit>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [auditStoreSubscriptionPayments, { isLoading }] =
    useAuditStoreSubscriptionPaymentsMutation();

  const audit = async (target: StoreSubscriptionVerificationTarget) => {
    setTarget(target);
    setResult(undefined);
    setErrorMessage(undefined);

    try {
      const auditResult = await auditStoreSubscriptionPayments({
        userId: target.userId,
        subscriptionId: target.subscriptionId,
      }).unwrap();
      setResult(auditResult);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  return {
    audit,
    isLoading,
    modalProps: {
      isOpen: Boolean(target),
      provider: target?.provider,
      isLoading,
      result,
      errorMessage,
      onClose: () => setTarget(undefined),
    },
  };
};
